import * as React from "react";
import * as ReactDOM from "react-dom";
import { getClient as getFeatureManagementClient } from "VSS/FeatureManagement/RestClient";
import { registerLWPComponent } from "VSS/LWP";
import { handleError } from "VSS/VSS";
import { LWPComponent } from "VSSPreview/Flux/Components/LWP";
import { ActionUrl } from "WorkItemTracking/Scripts/ActionUrls";
import { WorkItem } from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import * as WITControls_Async from "WorkItemTracking/Scripts/TFS.WorkItemTracking.Controls";
import { WitFormModeUtility } from "WorkItemTracking/Scripts/Utils/WitControlMode";
import { WorkItemActions } from "WorkItemTracking/Scripts/Utils/WorkItemControlsActions";
import VSS = require("VSS/VSS");
import Performance = require("VSS/Performance");

import Events_Action = require("VSS/Events/Action");

import TFS_Host_TfsContext = require("Presentation/Scripts/TFS/TFS.Host.TfsContext");

/** Keep track of required module to allow sub-sequent calls to be synchronous */
let witControlsModule: typeof WITControls_Async = null;

/**
 * This can safely be module-scoped because a preview feature change will cause a page reload
 */
const newWitHubsEnabledPromise = getFeatureManagementClient()
    .getFeatureState("ms.vss-work-web.new-boards-hub-feature", "me")
    .then((state) => state.state === 1);

/**
 * Open the given work item in a dialog
 * @param workItem Work item to open
 * @param options Optional parameters for dialog
 */
export async function showWorkItem(workItem: WorkItem, options?: WITControls_Async.IWorkItemDialogOptions) {
    if (await newWitHubsEnabledPromise) {
        // some OWP pages call this method to create a new work item
        const arg = workItem.id < 1 ? workItem.workItemType.name : workItem.id;
        const initialValues: { [fieldName: string]: any } = {};

        // new platform expects [fieldReferenceName, value]
        for (const key of Object.keys(workItem.fieldUpdates)) {
            const field = workItem.getField(Number(key));
            const fieldUpdate = workItem.fieldUpdates[key];

            // Pull out manually set field updates to pass to the new form
            // The rules engine should be the same between new and old, so fields set by rule should automatically get
            // populated in the new IWorkItem identically to the old IWorkItem
            if (field.fieldDefinition.referenceName && fieldUpdate.value && !fieldUpdate.setByRule) {
                initialValues[field.fieldDefinition.referenceName] = fieldUpdate.value;
            }
        }

        renderNewWebPlatformWorkItemDialog(arg, options, initialValues);
        return;
    }

    ensureModules((witModule) => {
        witModule.WorkItemFormDialog.showWorkItem(workItem, options);
    });
}

/**
 * Open the work item identified by the given id
 * @param workItemId Id of work item to open
 * @param options Optional parameters for dialog
 */
export function showWorkItemById(workItemId: number, options?: any): void;
/**
 * Open the work item identified by the given id
 * @param workItemId Id of work item to open
 * @param tfsContext Tfs context to use
 * @param options Optional parameters for dialog
 */
export function showWorkItemById(workItemId: number, tfsContext: TFS_Host_TfsContext.TfsContext, options?: any): void;
export async function showWorkItemById(workItemId: number, tfsContext: TFS_Host_TfsContext.TfsContext | any, options?: any) {
    if (await newWitHubsEnabledPromise) {
        renderNewWebPlatformWorkItemDialog(workItemId, options);
        return;
    }

    let tfsContextToUse: TFS_Host_TfsContext.TfsContext;
    let optionsToUse: any;

    if (options !== undefined) {
        tfsContextToUse = tfsContext || TFS_Host_TfsContext.TfsContext.getDefault();
        optionsToUse = options;
    } else {
        tfsContextToUse = TFS_Host_TfsContext.TfsContext.getDefault();
        optionsToUse = tfsContext;
    }

    ensureModules(() => {
        Events_Action.getService().performAction(WorkItemActions.ACTION_WORKITEM_OPEN, {
            id: workItemId,
            tfsContext: tfsContextToUse,
            options: optionsToUse
        });
    });
}

export async function showWorkItemDialogById(
    workItemId: number,
    tfsContext: TFS_Host_TfsContext.TfsContext,
    options?: WITControls_Async.IWorkItemDialogOptions,
    tryGetLatest?: boolean
) {
    if (await newWitHubsEnabledPromise) {
        renderNewWebPlatformWorkItemDialog(workItemId, options, undefined, tryGetLatest);
        return;
    }

    ensureModules(() => {
        Events_Action.getService().performAction(WorkItemActions.ACTION_WORKITEM_OPEN_DIALOG, {
            id: workItemId,
            tfsContext: tfsContext || TFS_Host_TfsContext.TfsContext.getDefault(),
            options: options,
            tryGetLatest: tryGetLatest
        });
    });
}

export async function showNewWorkItemDialog(
    workItemTypeName: string,
    initialValues: { [fieldName: string]: any },
    tfsContext: TFS_Host_TfsContext.TfsContext,
    options?: WITControls_Async.IWorkItemDialogOptions
) {
    if (await newWitHubsEnabledPromise) {
        renderNewWebPlatformWorkItemDialog(workItemTypeName, options, initialValues);
        return;
    }

    ensureModules(() => {
        Events_Action.getService().performAction(WorkItemActions.ACTION_WORKITEM_NEW_DIALOG, {
            workItemTypeName: workItemTypeName,
            tfsContext: tfsContext || TFS_Host_TfsContext.TfsContext.getDefault(),
            initialValues: initialValues,
            options: options
        });
    });
}

/**
 * Open the work item identified by the given id in a new tab
 * @param workItemId Id of work item to open
 * @param tfsContext Tfs context to use
 */
export function showWorkItemByIdInNewTab(workItemId: number, tfsContext?: TFS_Host_TfsContext.TfsContext): void {
    tfsContext = tfsContext || TFS_Host_TfsContext.TfsContext.getDefault();

    const url = tfsContext.getPublicActionUrl(ActionUrl.ACTION_EDIT, "workitems", { parameters: workItemId });
    window.open(url);
}

/**
 * Create a new work item
 * @param workItemTypeName Name of work item type to create new work item from
 * @param tfsContext Tfs context to use
 * @param initialValues Optional initial values to set
 */
export async function createNewWorkItem(
    workItemTypeName: string,
    tfsContext?: TFS_Host_TfsContext.TfsContext,
    initialValues?: { [fieldName: string]: any }
) {
    if (await newWitHubsEnabledPromise) {
        renderNewWebPlatformWorkItemDialog(workItemTypeName, undefined, initialValues);
        return;
    }

    tfsContext = tfsContext || TFS_Host_TfsContext.TfsContext.getDefault();

    ensureModules(() => {
        Events_Action.getService().performAction(WorkItemActions.ACTION_WORKITEM_NEW, {
            workItemTypeName: workItemTypeName,
            tfsContext: tfsContext,
            initialValues: initialValues
        });
    });
}

interface INewWorkItemDialogComponentProps {
    workItemTypeName: string;
    options?: WITControls_Async.IWorkItemDialogOptions;
    onDismiss: () => void;
    initialValues?: { [fieldName: string]: any };
}

class NewWorkItemDialogComponent extends React.Component<INewWorkItemDialogComponentProps, {}> {
    public static componentType = "createNewWorkItemDialog";

    public render(): null {
        return null;
    }
    public componentDidMount(): void {
        const { workItemTypeName, onDismiss, initialValues } = this.props;
        const options = this.props.options || {};

        showNewWorkItemDialog(workItemTypeName, initialValues, null, {
            ...options,
            close: (workItem: WorkItem) => {
                if (options.close) {
                    options.close(workItem);
                }
                onDismiss();
            }
        });
    }
}

registerLWPComponent(NewWorkItemDialogComponent.componentType, NewWorkItemDialogComponent);

interface IWorkItemDialogComponentProps {
    workItemId: number;
    options?: WITControls_Async.IWorkItemDialogOptions;
    tryGetLatest?: boolean;
    onDismiss: () => void;
}

class WorkItemDialogComponent extends React.Component<IWorkItemDialogComponentProps, {}> {
    public static readonly componentType = "WorkItemDialog";

    public render(): null {
        return null;
    }

    public componentDidMount(): void {
        const { workItemId, onDismiss } = this.props;
        const options = this.props.options || {};

        showWorkItemDialogById(
            workItemId,
            null,
            {
                ...options,
                close: (workItem: WorkItem) => {
                    if (options.close) {
                        options.close(workItem);
                    }

                    onDismiss();
                }
            },
            this.props.tryGetLatest
        );
    }
}

registerLWPComponent(WorkItemDialogComponent.componentType, WorkItemDialogComponent);

/**
 * Prefetch scripts required for displaying a work item form
 * @param tfsContext Optional tfs context to determine form state
 */
export function prefetchFormModules(tfsContext: TFS_Host_TfsContext.TfsContext = TFS_Host_TfsContext.TfsContext.getDefault()): IPromise<void> {
    return determineModulesToLoad().then((modules) => VSS.requireModules(modules));
}

function ensureModules(callback: (mod: typeof WITControls_Async) => void) {
    if (!witControlsModule) {
        const witFormScenario: Performance.IScenarioDescriptor = Performance.getScenarioManager().startScenario("WIT", "FormShim.LoadModules");

        determineModulesToLoad().then(
            (modules) =>
                VSS.requireModules(modules).spread((WITControls: typeof WITControls_Async) => {
                    witControlsModule = WITControls;

                    callback(witControlsModule);

                    witFormScenario.end();
                }),
            handleError
        );
    } else {
        const witFormScenario: Performance.IScenarioDescriptor = Performance.getScenarioManager().startScenario("WIT", "FormShim");
        callback(witControlsModule);
        witFormScenario.end();
    }
}

function determineModulesToLoad(): IPromise<string[]> {
    return WitFormModeUtility.ensureWitFormModeLoaded().then(() => {
        return ["WorkItemTracking/Scripts/TFS.WorkItemTracking.Controls"];
    });
}

function renderNewWebPlatformWorkItemDialog(
    arg: number | string,
    options?: WITControls_Async.IWorkItemDialogOptions,
    initialValues?: { [fieldName: string]: any },
    tryGetLatest?: boolean
): void {
    // Create a top level element for the panel to be hosted inside.
    let calloutElement: HTMLDivElement | null = document.createElement("div");
    document.body.appendChild(calloutElement);

    // Have a local function for closing the callout.
    const onDismiss = (): void => {
        if (calloutElement === null) {
            return;
        }

        ReactDOM.unmountComponentAtNode(calloutElement);
        document.body.removeChild(calloutElement);
        calloutElement = null;
    };

    const workItemFormProps =
        typeof arg === "number"
            ? // If the argument is a number, then it is a work item id
              {
                  workItemId: arg,
                  onDismiss,
                  onLegacyDismiss: options?.close,
                  onLegacySave: options?.save,
                  onLegacyDelete: options?.deleteItem,
                  readOnly: options?.readOnly,
                  shouldRefresh: tryGetLatest
              }
            : // If the argument is a string, then it is a work item type
              {
                  workItemTypeName: arg,
                  initialValues,
                  onDismiss,
                  onLegacyDismiss: options?.close,
                  onLegacySave: options?.save,
                  onLegacyDelete: options?.deleteItem,
                  readOnly: options?.readOnly
              };

    // Render the root component with the results of the callout as the children.
    ReactDOM.render(
        React.createElement(LWPComponent, {
            wrappedType: "work-item-form-dialog",
            dependencies: ["ms.vss-work-web.work-item-dialogs"],
            ...workItemFormProps
        }),
        calloutElement
    );
}
